import type { Row, RowAmounts, Employee, StubLine, PayMethod } from './types';

export function getToken(): string {
  return localStorage.getItem('access_token') || localStorage.getItem('token') || '';
}

export function authHeaders(): Record<string, string> {
  return {
    Authorization: 'Bearer ' + getToken(),
    'Content-Type': 'application/json',
  };
}

export function fmtMoney(n: number | string | null | undefined): string {
  if (n == null || isNaN(Number(n))) return '$0.00';
  return '$' + Number(n).toLocaleString('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function fmtDateShort(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function fmtDateWithWeekday(iso: string | null | undefined): string {
  if (!iso) return '-';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('en-CA', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function stripHourZeros(val: number | string | null | undefined): string {
  if (val == null || val === '') return '';
  let s = String(val);
  if (s.indexOf('.') === -1) return s;
  s = s.replace(/\.?0+$/, '');
  return s === '' ? '0' : s;
}

// Single source of truth for every money and hours figure on a row.
// Used by the row itself, the summary cards, and the readiness banner
// so the numbers can never drift apart.
export function rowAmounts(r: Row): RowAmounts {
  const regular = parseFloat(r.regular) || 0;
  const overtime = parseFloat(r.overtime) || 0;
  const vacation = parseFloat(r.vacation) || 0;
  const sick = parseFloat(r.sick) || 0;
  const stat = parseFloat(r.statHoliday) || 0;
  const rate = Number(r.hourlyRate) || 0;
  const statDaily = Number(r.statAvgDaily) || 0;
  const regPay = regular * rate;
  const otPay = overtime * rate * 1.5;
  const vacPay = vacation * rate;
  const sickPay = sick * rate;
  const statPay = stat * (statDaily / 8);
  return {
    regular, overtime, vacation, sick, stat, rate,
    regPay, otPay, vacPay, sickPay, statPay,
    extraHours: overtime + vacation + sick,
    totalHours: regular + overtime + vacation + sick + stat,
    gross: regPay + otPay + vacPay + sickPay + statPay,
  };
}

export function mapEmployeeToRow(e: Employee, line: StubLine | undefined): Row {
  const eid = (e.id || e.employee_id) as string | number;
  const first = e.preferred_name || e.first_name || '';
  const last = e.last_name || '';
  const name = (last && first) ? (last + ', ' + first) : (first || last || 'Unnamed');
  const rate = e.hourly_rate || e.pay_rate || e.rate;

  const l = line || {};
  const hoursRegularVal = stripHourZeros(l.hours_regular);
  const hoursOvertimeVal = stripHourZeros(l.hours_overtime);
  const hoursStatVal = stripHourZeros(l.hours_stat_holiday);
  const hoursVacationVal = stripHourZeros(l.hours_vacation);
  const hoursSickVal = stripHourZeros(l.hours_sick);

  const setupComplete = e.setup_complete !== false;
  const empStatus = e.employment_status || 'active';
  const isInactive =
    empStatus === 'terminated' ||
    empStatus === 'deceased' ||
    empStatus === 'not_on_payroll' ||
    empStatus === 'unpaid_leave';
  const canBePaid = setupComplete && !isInactive;

  const payMethodRaw = (e.default_pay_method || e.pay_method || 'direct_deposit').toString().toLowerCase();
  const payMethod: PayMethod =
    payMethodRaw.includes('cheque') || payMethodRaw.includes('check') ? 'Cheque' : 'Direct deposit';

  const toStrOrEmpty = (v: string | null | undefined): string =>
    v != null && Number(v) > 0 ? String(v) : '';

  return {
    id: eid,
    name,
    position: e.position_title || '',
    hourlyRate: rate ? Number(rate) : 0,
    regular: toStrOrEmpty(hoursRegularVal),
    overtime: toStrOrEmpty(hoursOvertimeVal),
    statHoliday: toStrOrEmpty(hoursStatVal),
    vacation: toStrOrEmpty(hoursVacationVal),
    sick: toStrOrEmpty(hoursSickVal),
    statAvgDaily: l.stat_pay_avg != null ? (l.stat_pay_avg as number | string) : '',
    payMethod,
    ready: canBePaid,
    included: canBePaid,
    skipped: false,
    employment_status: empStatus,
    memo: l.memo || '',
  };
}